import { hash } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import { t } from 'ember-intl';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

import { getBem } from 'umg-ember-backend/utils/get-bem';

import Group from './group';
import Item from './item';
import styles from './styles.module.css';

const bem = getBem(styles);

function filterNavigationChildren(children, canViewNavigation) {
  const filteredChildren = [];
  let pendingSection;

  for (const child of children) {
    if (child.type === 'section') {
      pendingSection = child;
      continue;
    }

    if (canViewNavigation(child.key)) {
      if (pendingSection) {
        filteredChildren.push(pendingSection);
        pendingSection = undefined;
      }

      filteredChildren.push(child);
    }
  }

  return filteredChildren;
}

const NAVIGATION_ITEMS = [
  { key: 'dashboard', route: 'admin.dashboard' },
  { key: 'mlp' },
  {
    key: 'content',
    children: [
      { key: 'article' },
      { key: 'collection' },
      { key: 'teaser' },
      { key: 'story' },
      { key: 'media-embed' },
      { key: 'audioplayer' },
      { key: 'job' },
      { key: 'action' },
      { key: 'contest' },
      {
        type: 'section',
        key: 'application.navigation.section.structure-and-metadata',
      },
      { key: 'taxonomy' },
    ],
  },
  {
    key: 'artists-and-products',
    children: [
      { key: 'artist' },
      { key: 'product' },
      { key: 'product-family' },
      { key: 'web-series' },
      { key: 'label' },
      {
        type: 'section',
        key: 'application.navigation.section.tools-and-import',
      },
      {
        key: 'tool-merge-artists',
        route: 'admin.tool-merge-artists',
      },
      {
        key: 'tool-mmc-importer',
        route: 'admin.tool-mmc-importer',
      },
      {
        key: 'tool-itunes-import',
        route: 'admin.tool-itunes-import',
      },
      {
        key: 'tool-itunes-ids-uploader',
        route: 'admin.tool-itunes-ids-uploader',
      },
    ],
  },
  {
    key: 'assets',
    children: [
      { key: 'asset' },
      {
        type: 'section',
        key: 'application.navigation.section.configuration',
      },
      { key: 'asset-format' },
      { key: 'asset-token-config' },
    ],
  },
  {
    key: 'events',
    children: [
      { key: 'live-event' },
      { key: 'media-event' },
      { key: 'calendar-event' },
    ],
  },
  {
    key: 'short-urls-and-links',
    children: [{ key: 'short-url' }, { key: 'link' }],
  },
  {
    key: 'd2c',
    children: [{ key: 'digital-order-config' }, { key: 'shopify-store' }],
  },
  {
    key: 'accessify',
    children: [
      { key: 'a11y-quickscan', route: 'admin.a11y-quickscan' },
      { key: 'a11y-scanner' },
    ],
  },
  { key: 'frontend-user' },
  {
    key: 'partners',
    children: [
      { key: 'trade-partner' },
      { key: 'media-partner' },
      { key: 'live-partner' },
      { key: 'coop-partner' },
    ],
  },
  {
    key: 'customer-support',
    collapseSingleChild: false,
    children: [
      { key: 'tool-reset-voucher', route: 'admin.tool-reset-voucher' },
    ],
  },
  {
    key: 'administration',
    children: [
      { key: 'channel' },
      { key: 'organization' },
      { key: 'division' },
      { key: 'vhost' },
      { key: 'sftp-user' },
      { key: 'domain' },
      { key: 'url-alias' },
      { key: 'link-pattern' },
      { key: 'category' },
      { key: 'user' },
      { key: 'user-role' },
      { key: 'person' },
      { key: 'section' },
    ],
  },
];
export default class ApplicationNavigationComponent extends Component {
  // Services
  @service abilities;

  get filteredNavigationItems() {
    let items = JSON.parse(JSON.stringify(NAVIGATION_ITEMS));

    return items.filter((item) => {
      if (item.children) {
        item.children = filterNavigationChildren(item.children, (key) => {
          return this.abilities.can('viewNavigation', key);
        });

        const navigationChildren = item.children.filter(
          (child) => child.type !== 'section',
        );

        if (
          item.children.length === 1 &&
          item.children[0].type !== 'section' &&
          item.collapseSingleChild !== false
        ) {
          item.key = navigationChildren[0].key;
          if (navigationChildren[0].route) {
            item.route = navigationChildren[0].route;
          }
          delete item.children;

          return true;
        }

        return Boolean(navigationChildren.length);
      }

      return this.abilities.can('viewNavigation', item.key);
    });
  }

  // Templates
  <template>
    <nav class={{bem (hash is-open=@isOpen)}} ...attributes>
      <ul class={{bem 'main-navigation'}}>
        {{#each this.filteredNavigationItems as |item|}}
          {{#if item.children}}
            <Group @group={{item}} @onCloseNavigation={{@onCloseNavigation}} />
          {{else}}
            <Item @item={{item}} @onCloseNavigation={{@onCloseNavigation}} />
          {{/if}}
        {{/each}}
      </ul>
      <div class={{bem 'external-tools'}}>
        <p class={{bem 'external-title'}}>{{t
            'application.navigation.tools.title'
          }}</p>
        <a
          href='https://beta.projx.de/'
          target='_blank'
          rel='noopener noreferrer'
          class={{bem 'external-tool'}}
        >
          {{t 'application.navigation.tools.seo'}}
          {{svgJar 'external-link' class=(bem 'external-icon')}}
        </a>
        <a
          href='https://domains.universal-music.de/order'
          target='_blank'
          rel='noopener noreferrer'
          class={{bem 'external-tool'}}
        >
          {{t 'application.navigation.tools.domain'}}
          {{svgJar 'external-link' class=(bem 'external-icon')}}
        </a>
        <a
          href='https://wow.universal-music.de/auth/redirect'
          target='_blank'
          rel='noopener noreferrer'
          class={{bem 'external-tool'}}
        >
          {{t 'application.navigation.tools.contact'}}
          {{svgJar 'external-link' class=(bem 'external-icon')}}
        </a>
      </div>
    </nav>
  </template>
}
